/* External */
import * as React from 'react';
import { findDOMNode } from 'react-dom';

/* Utils */
import {
  getElementVisibleArea,
  isElementPartiallyVisibleAndOnTop,
  isFullyVisible,
  getElementTopLeftPosition,
} from '../../utils/dom';
import { RestorableFocusContext } from './contexts';

const { PureComponent } = React;

type MouseEnterCallback = (event: React.MouseEvent<HTMLElement>) => void;

export type WithFocusRestorableInjectedProps = {
  realFocusKey: string,
  onMouseEnter: MouseEnterCallback
};

type WithFocusRestorableProps<Props> = Props & {
  realFocusKey: string,
  focusRestorable?: boolean,
  ignoreHoverToRestoreFocus?: boolean,
  onMouseEnter?: MouseEnterCallback,
  onFocusRestored?: (props: Props & WithFocusRestorableProps<Props>) => void
};

export interface FocusRestorableComponent {
  onFocusRestored(): void;
  getTopLeftPosition(): number;
  getVisibleArea(): number;
  ignoreHoverToRestoreFocus(): boolean;
  isFocusRestorable(): boolean;
  isFullyVisible(viewPortPadding?: number): boolean;
}

export default function withFocusRestorable<Props extends Record<string, unknown>>(
  WrappedComponent: React.ComponentType<Props & WithFocusRestorableInjectedProps>,
) {
  class WithFocusRestorable
    extends PureComponent<WithFocusRestorableProps<Props>>
    implements FocusRestorableComponent {
    // eslint-disable-next-line react/static-property-placement -- false positive
    context: React.ContextType<typeof RestorableFocusContext> = undefined;

    domNode?: Element;

    componentDidMount() {
      /* eslint-disable-next-line react/no-find-dom-node
          -- We rely on the DOM node to find best option to restore focus */
      this.domNode = findDOMNode(this) as Element;
      const { realFocusKey } = this.props;
      const { addFocusableComponent } = this.context || {};
      if (addFocusableComponent) addFocusableComponent(realFocusKey, this);
    }

    componentDidUpdate() {
      /* eslint-disable-next-line react/no-find-dom-node
          -- We rely on the DOM node to find best option to restore focus */
      this.domNode = findDOMNode(this) as Element;
    }

    componentWillUnmount() {
      const { realFocusKey } = this.props;
      const { removeFocusableComponent } = this.context || {};
      if (removeFocusableComponent) removeFocusableComponent(realFocusKey);
    }

    /* eslint-disable react/no-unused-class-component-methods
        -- These methods are expected to be used by users of the HOC */
    onFocusRestored() {
      const { onFocusRestored } = this.props;
      if (onFocusRestored) {
        onFocusRestored(this.props);
      }
    }

    getTopLeftPosition(): number {
      if (!this.domNode) return 0;
      return getElementTopLeftPosition(this.domNode);
    }

    getVisibleArea(): number {
      if (!this.domNode) return 0;
      return getElementVisibleArea(this.domNode);
    }

    handleMouseEnter = (event: React.MouseEvent<HTMLElement>) => {
      const { realFocusKey, onMouseEnter } = this.props;
      const { setLastHoveredRealFocusKey } = this.context || {};
      if (setLastHoveredRealFocusKey) setLastHoveredRealFocusKey(realFocusKey);

      if (onMouseEnter) onMouseEnter(event);
    };

    ignoreHoverToRestoreFocus(): boolean {
      const { ignoreHoverToRestoreFocus } = this.props;
      return ignoreHoverToRestoreFocus === true;
    }

    isFocusRestorable(): boolean {
      const { focusRestorable } = this.props;

      if (focusRestorable !== undefined) return focusRestorable;

      if (!this.domNode) return false;
      return isElementPartiallyVisibleAndOnTop(this.domNode);
    }

    isFullyVisible(viewPortPadding?: number): boolean {
      if (!this.domNode) return false;
      return isFullyVisible(this.domNode, viewPortPadding);
    }
    /* eslint-enable react/no-unused-class-component-methods -- Re-enable */

    render() {
      const {
        realFocusKey,
        onFocusRestored,
        focusRestorable,
        ignoreHoverToRestoreFocus,
        onMouseEnter,
        ...restProps
      } = this.props;
      return (
        <WrappedComponent
          {...(restProps as unknown as Props)}
          realFocusKey={realFocusKey}
          onMouseEnter={this.handleMouseEnter}
        />
      );
    }
  }

  WithFocusRestorable.contextType = RestorableFocusContext;

  return WithFocusRestorable;
}
